import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ExternalLink, Loader2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API } from '@/App';

const PartnersPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await axios.get(`${API}/partners`);
        setPartners(res.data.filter(p => p.is_active !== false));
      } catch { /* fallback */ }
      setLoading(false);
    };
    fetchPartners();
  }, []);

  const partnersByType = {};
  partners.forEach(p => {
    const t = p.type || 'Autre';
    if (!partnersByType[t]) partnersByType[t] = [];
    partnersByType[t].push(p);
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="Nos Partenaires" description="Decouvrez les partenaires de la SFOALC, société savante en contactologie." />
      <div className="h-[72px]" />

      {/* Hero */}
      <section className="pt-12 pb-10 px-4" style={{ background: '#eaf6fc' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="partners-title">
            Nos Partenaires
          </h1>
          <p className="text-gray-600 text-lg">
            La SFOALC collabore avec des institutions de reference dans le domaine de l'ophtalmologie et de la contactologie.
          </p>
        </div>
      </section>

      {/* Partners List */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#35AFE4]" size={28} /></div>
          ) : Object.keys(partnersByType).length === 0 ? (
            <p className="text-center text-gray-500 py-12">Aucun partenaire pour le moment.</p>
          ) : (
            Object.entries(partnersByType).map(([type, list]) => (
              <div key={type} className="mb-12" data-testid={`partner-type-${type}`}>
                <h2 className="text-lg font-bold text-[#1e3a5f] mb-6 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <div className="w-8 h-1 bg-[#35AFE4] rounded-full" />
                  Partenaires {type}s
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map(partner => (
                    <div key={partner.id} className="group border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-gray-200 transition-all flex flex-col items-center text-center" data-testid={`partner-${partner.id}`}>
                      <div className="w-28 h-28 flex items-center justify-center mb-5 rounded-xl bg-gray-50 p-3">
                        {partner.logo_url ? (
                          <img
                            src={partner.logo_url}
                            alt={partner.name}
                            className="max-w-full max-h-full object-contain"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center text-white text-2xl font-bold">
                            {partner.name?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{partner.name}</h3>
                      <p className="text-sm text-gray-500 mb-4 flex-1">{partner.description}</p>
                      {partner.website_url && partner.website_url !== '#' && (
                        <a
                          href={partner.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#35AFE4] hover:text-[#1e3a5f] transition-colors"
                        >
                          Visiter le site <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-[#1e3a5f] text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Devenir partenaire
          </h2>
          <p className="text-white/70 mb-6">Vous souhaitez collaborer avec la SFOALC ? Contactez-nous.</p>
          <Button onClick={() => navigate('/contact')} className="bg-white text-[#1e3a5f] hover:bg-gray-100 font-semibold px-8 py-3 rounded-xl" data-testid="partners-contact-btn">
            Nous contacter
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
